import * as THREE from 'three';
import InteractControls from './three-extensions/InteractControls.js';
import { OrbitControls } from './three-extensions/OrbitControls.js';
import Layer from './Layer.js';
import Collection from './Collection.js';
import renderElement from './renderElement.js';
import Coordinate from './three-extensions/Coordinate.js';
import TWEEN from '@tweenjs/tween.js';
import { CSS2DRenderer } from 'three/examples/jsm/renderers/CSS2DRenderer';

const elementTypes = ['image', 'label', 'roi', 'heatMap', 'text', 'trackMark'];

// https://stackoverflow.com/questions/29884485/threejs-canvas-size-based-on-container
const getDomSize = (dom) => {
  return { width: dom.clientWidth, height: dom.clientHeight };
};

class Index extends THREE.EventDispatcher {
    // {
    //     LEFT,
    //     MIDDLE,
    //     RIGHT,
    //     ROTATE,
    //     DOLLY,
    //     PAN,
    // }
    static MOUSE = THREE.MOUSE;

    // {
    //     ROTATE,
    //     PAN,
    //     DOLLY_PAN,
    //     DOLLY_ROTATE,
    // }
    static TOUCH = THREE.TOUCH;

    static eventType = {
      // layerData
      layerDataChange: 'layerDataChange', // {type, layerData}
      // map
      mapDomResize: 'mapDomResize', // {type, width, height}
      mapMouseDown: 'mapMouseDown', // {type, mapPosition, domEvent}
      mapMouseUp: 'mapMouseUp', // {type, mapPosition, domEvent}
      mapMouseMove: 'mapMouseMove', // {type, mapPosition, domEvent}
      mapClick: 'mapClick', // {type, mapPosition, domEvent}
      // element
      elementMouseEnter: 'elementMouseEnter', // {type, elementProps}
      elementMouseLeave: 'elementMouseLeave', // {type, elementProps}
      elementClick: 'elementClick', // {type, elementProps}
    };
    // 生命周期
    constructor(dom) {
      super();
      this._dom = null;
      this._layer = null;
      this._scene = null;
      this._camera = null;
      this._renderer = null;
      this._labelRender = null; // 用于渲染CSS2DObject

      this._group = null;
      this._groupCoordinate = null;
      this._groupLayer = null;

      this._orbitControls = null;
      this._interactControls = null;
      this._elements = []; // 渲染树的所有 element

      // init three js
      const { width, height } = getDomSize(dom);
      this._dom = dom;
      this._scene = new THREE.Scene();
      this._camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 10000);
      this._camera.position.set(0, 0, 1000);
      // this._camera.lookAt(new THREE.Vector3(500, 500, 0));
      // this._renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, depth: !false, logarithmicDepthBuffer: true });
      this._renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, depth: true });
      // this._renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, depth: true, logarithmicDepthBuffer: true });
      this._renderer.setSize(width, height);
      this._scene.add(this._camera);
      this._dom.appendChild(this._renderer.domElement);

      // add group
      this._group = new THREE.Group();
      this._groupCoordinate = new THREE.Group();
      this._groupLayer = new THREE.Group();
      // this._groupLayer.position.set(0, 0, 100);
      this._group.add(this._groupCoordinate);
      this._group.add(this._groupLayer);
      this._scene.add(this._group);

      this._labelList = [];

      //
      this._labelRender = new CSS2DRenderer();
      this._labelRender.setSize(width, height);
      this._labelRender.domElement.style.position = 'absolute';
      this._labelRender.domElement.style.top = '0px';
      this._dom.appendChild(this._labelRender.domElement);
      // add resize listener
      window.addEventListener('resize', this._resize, false);
      // this.showCoordinate(); // 显示辅助坐标
      // call animate
      this._animate();

      // add controls
      this._orbitControls = new OrbitControls(this._camera, this._dom);
      this._orbitControls.mouseButtons = {
        LEFT: THREE.MOUSE.PAN,
        MIDDLE: THREE.MOUSE.DOLLY,
        RIGHT: THREE.MOUSE.ROTATE
      };

      // 监听所有 element，不递归监听其 element 的 child
      this._interactControls = new InteractControls(this._camera, this._dom, this._elements, false);

      // map
      this._interactControls.addEventListener('mousedown', (e) => {
        const { mapPosition, domEvent } = e;
        this.dispatchEvent({ type: Index.eventType.mapMouseDown, mapPosition, domEvent });
      });
      this._interactControls.addEventListener('mouseup', (e) => {
        const { mapPosition, domEvent } = e;
        this.dispatchEvent({ type: Index.eventType.mapMouseUp, mapPosition, domEvent });
      });
      this._interactControls.addEventListener('mousemove', (e) => {
        const { mapPosition, domEvent } = e;
        this.dispatchEvent({ type: Index.eventType.mapMouseMove, mapPosition, domEvent });
      });
      this._interactControls.addEventListener('click', (e) => {
        const { mapPosition, domEvent } = e;
        this.dispatchEvent({ type: Index.eventType.mapClick, mapPosition, domEvent });
      });
      // element
      this._interactControls.addEventListener('mouseenter', (e) => {
        this.dispatchEvent({ type: Index.eventType.elementMouseEnter, elementProps: e.object.props });
      });
      this._interactControls.addEventListener('mouseleave', (e) => {
        this.dispatchEvent({ type: Index.eventType.elementMouseLeave, elementProps: e.object.props });
      });
      this._interactControls.addEventListener('detected', (e) => {
        this.dispatchEvent({ type: Index.eventType.elementClick, elementProps: e.object.props });
      });
      if (this._onload) {
        this._onload();
      } else {
        this._onload = true;
      }
    }

    get onload() {
      return this._onload;
    }

    set onload(value) {
      if (this._onload) {
        value();
      }
      this._onload = value;
    }

    dispose() {
      this._interactControls.dispose();
      this._orbitControls.dispose();
      window.removeEventListener('resize', this._resize, false);
    }

    getMapSize() {
      return getDomSize(this._dom);
    }

    // layerData
    setLayerData(data) {
      const layer = new Layer(data, this._camera);
      if (!this._layer) {
        // remove old layer and add new layer
        this._groupLayer.remove(...this._groupLayer.children);
        this._layer = layer;
        this._groupLayer.add(this._layer);
        const { minZoom, maxZoom } = this._layer.props;
        const cameraPositionMinZ = Layer.zoomToCameraPositionZ(maxZoom);
        const cameraPositionMaxZ = Layer.zoomToCameraPositionZ(minZoom);
        this._orbitControls.minDistance = cameraPositionMinZ;
        this._orbitControls.maxDistance = cameraPositionMaxZ;
        // this._mapControls.setCameraMinMaxZ(cameraPositionMinZ, cameraPositionMaxZ);

        this._layerDataChange();
        this._resize();
        this._resetElements();
      } else {
        const duration = 1000;
        const property = {
          scale: 1
        };
        const target = {
          scale: 0.1
        };
        new TWEEN.Tween(property).to(target, duration)
          .easing(TWEEN.Easing.Bounce.Out)
          .onUpdate(() => {
            this._layer.scale.set(property.scale, property.scale, 1);
          })
          .onComplete(() => {
            const property2 = {
              scale: 0.1
            };
            const target2 = {
              scale: 1
            };
            // remove old layer and add new layer
            this._groupLayer.remove(...this._groupLayer.children);
            this._layer = layer;

            this._layer.scale.set(property2.scale, property2.scale, 1);
            this._groupLayer.add(this._layer);

            const { minZoom, maxZoom } = this._layer.props;
            const cameraPositionMinZ = Layer.zoomToCameraPositionZ(maxZoom);
            const cameraPositionMaxZ = Layer.zoomToCameraPositionZ(minZoom);
            this._orbitControls.minDistance = cameraPositionMinZ;
            this._orbitControls.maxDistance = cameraPositionMaxZ;

            this._layerDataChange();
            this._resize();
            this._resetElements();

            new TWEEN.Tween(property2).to(target2, duration)
              .easing(TWEEN.Easing.Bounce.In)
              .onUpdate(() => {
                this._layer.scale.set(property2.scale, property2.scale, 1);
              })
              .start();
          })
          .start();
      }
    }

    getLayerData() {
      return this._layer?.getData();
    }

    // clearLayer() {
    //   this._elements.map((item) => {
    //     console.log('item', item);
    //     if (item.label) {
    //       item.remove(item.label);
    //     }
    //     this.delElement(item.props.id);
    //   });
    // }

    // 操作渲染节点
    addCollection(layerId, collectionData) {
      const target = this._getNodeByPropsId(layerId);
      const collection = new Collection(collectionData);
      target.add(collection);
      // 新增的collection中看是否有新增线条
      const LineElment = collection.getLineElement();
      const lineElms = target.getLineElements() || [];
      LineElment && target.setLineElements([...lineElms, LineElment]);
      this._layerDataChange();
      this._resetElements();
    }

    delCollection(collectionId) {
      this._getNodeByPropsId(collectionId)?.removeFromParent();
      this._layerDataChange();
      this._resetElements();
    }

    clearCollection(collectionId) {
      const target = this._getNodeByPropsId(collectionId);
      target.remove(...target.children);
      this._layerDataChange();
      this._resetElements();
    }

    /**
     * 更新 collection 的 children; children 中元素必须是相同类型，方便使用缓存
     * @param {*} collectionId
     * @param {*} childrenData Array
     */
    updateCollectionChildren(collectionId, childrenData) {
      // delete all children
      if (childrenData.length === 0) {
        this.clearCollection(collectionId);
        this._layerDataChange();
        this._resetElements();
        return;
      }

      const collection = this._getNodeByPropsId(collectionId);
      const { children } = collection.getData();

      const dCount = childrenData.length - children.length;
      // only set props of children
      if (dCount === 0) {
        for (let i = 0; i < children.length; i++) {
          this._getNodeByPropsId(children[i].props.id).setProps(childrenData[i].props);
        }
      }
      // add several
      if (dCount > 0) {
        for (let i = 0; i < children.length; i++) {
          this._getNodeByPropsId(children[i].props.id).setProps(childrenData[i].props);
        }
        for (let i = children.length; i < childrenData.length; i++) {
          const elementData = childrenData[i];
          this.addElement(collectionId, elementData);
        }
      }
      // delete several
      if (dCount < 0) {
        for (let i = 0; i < childrenData.length; i++) {
          this._getNodeByPropsId(children[i].props.id).setProps(childrenData[i].props);
        }
        for (let i = childrenData.length; i < children.length; i++) {
          const elementData = children[i];
          this.delElement(elementData.props.id);
        }
      }
      this._layerDataChange();
      this._resetElements();
    }

    moveToPoint({ x, y, duration = 1000 }) {
      new TWEEN.Tween({ x1: this._camera.position.x, y1: this._camera.position.y }).to({ x1: x, y1: y }, duration)
        .onUpdate((obj) => {
          this._camera.position.x = obj.x1;
          this._camera.position.y = obj.y1;
          this._orbitControls.target.x = obj.x1;
          this._orbitControls.target.y = obj.y1;
        })
        // .onComplete(() => {
        //   console.log(this._camera);
        // })
        .start();
    }

    // 增加返回值
    addElement(collectionId, elementData) {
      const target = this._getNodeByPropsId(collectionId);
      target.add(renderElement(elementData));
      this._layerDataChange();
      this._resetElements();
    }

    delElement(elementId) {
      const node = this._getNodeByPropsId(elementId);
      node?.label && node.remove(node.label); // 删除标签附带的所有label
      this._resetElements();
      node?.removeFromParent();
      this._layerDataChange();
      this._resetElements();
    }

    updateProps(id, key, value) {
      const node = this._getNodeByPropsId(id);
      if (node) {
        this._getNodeByPropsId(id).updateProps(key, value);
      }
      this._layerDataChange();
      this._resetElements();
    }

    // 将 dom 的 clientX，clientY 转为 map 坐标系下的 {x, y}
    clientToMapPosition = (x, y) => {
      const rect = this._dom.getBoundingClientRect();

      // Standard x and y in standard coordinate, range from [-1, 1]
      const standardX = ((x - rect.left) / rect.width) * 2 - 1;
      const standardY = -((y - rect.top) / rect.height) * 2 + 1;

      // Get identity vector from camera to target point
      const vec = new THREE.Vector3().set(standardX, standardY, 0.5);
      vec.unproject(this._camera);
      vec.sub(this._camera.position).normalize();

      // Get vector from camera to XY plane
      vec.multiplyScalar(-this._camera.position.z / vec.z);

      // Get intersect on XY plane
      const pos = new THREE.Vector3().copy(this._camera.position).add(vec);

      return { x: pos.x, y: pos.y };
    }

    //  map 坐标系下的 {x, y} 转为 dom 的 offsetX，offsetY
    mapPositionToOffset = (x, y) => {
      const { width, height } = getDomSize(this._dom);
      const vector = new THREE.Vector3().set(x, y, 0);
      vector.project(this._camera);
      vector.x = (vector.x + 1) * width / 2;
      vector.y = -(vector.y - 1) * height / 2;
      vector.z = 0;
      return { x: vector.x, y: vector.y };
    }

    // transform
    /**
     * 设置 transform 时鼠标键的对应
     * @param {*} value
     * 例如：{
            LEFT: Index.MOUSE.ROTATE,
            MIDDLE: Index.MOUSE.DOLLY,
            RIGHT: Index.MOUSE.PAN
        }
     */
    setTransformMouseButtons(value) {
      this._orbitControls.mouseButtons = value;
    }

    /**
    * 设置 transform 时 touch 的对应
    * @param {*} value
    * 例如：{
           ONE: Index.TOUCH.ROTATE,
           TWO: TOUCH.DOLLY_PAN
       }
    */
    setTransformTouches(value) {
      this._orbitControls.touches = value;
    }

    setTransformEnabled(value) {
      this._orbitControls.enabled = value;
    }

    getTransformEnabled() {
      return this._orbitControls.enabled;
    }

    setZoomEnabled(value) {
      this._orbitControls.enableZoom = value;
    }

    getZoomEnabled() {
      return this._orbitControls.enableZoom;
    }

    setPanEnabled(value) {
      this._orbitControls.enablePan = value;
    }

    getPanEnabled() {
      return this._orbitControls.enablePan;
    }

    setRotateEnabled(value) {
      this._orbitControls.enableRotate = value;
    }

    getRotateEnabled() {
      return this._orbitControls.enableRotate;
    }

    // zoom
    zoomIn = () => {
      if (this._layer) {
        const { zoom, minZoom, maxZoom } = this._layer.props;
        const value = zoom + 0.5;
        if (value >= minZoom && value <= maxZoom) {
          this._layer.updateProps('zoom', value);
          this._layerDataChange();
        }
      }
    }

    zoomOut = () => {
      if (this._layer) {
        const { zoom, minZoom, maxZoom } = this._layer.props;
        const value = zoom - 0.5;
        if (value >= minZoom && value <= maxZoom) {
          this._layer.updateProps('zoom', value);
          this._layerDataChange();
        }
      }
    }

    // 辅助坐标系
    showCoordinate() {
      const coordinate = new Coordinate(1000, 1000, 100);
      coordinate.renderOrder = 100;
      this._group.add(coordinate);
    }

    hideCoordinate() {
      this._groupCoordinate.remove(...this._groupCoordinate.children);
    }

    // 内部函数
    _animate = () => {
      requestAnimationFrame(this._animate);
      // this._dom.appendChild(this._labelRender.domElement);
      this._renderer.render(this._scene, this._camera);
      this._labelRender.render(this._scene, this._camera);
      TWEEN.update();
      // 静态 object，一直正对 camera
      // if (this.layerRendered) {
      //     this.layerRendered.getStaticElementRendered().forEach(e => {
      //         e.rotation.copy(this.camera.rotation);
      //         e.updateMatrix();
      //     });
      // }
    };

    _resize = () => {
      const { width, height } = getDomSize(this._dom);

      if (width * height !== 0) {
        this._camera.aspect = width / height;
        this._camera.updateProjectionMatrix();
        this._renderer.setSize(width, height);
        this._labelRender.setSize(width, height);
        this.dispatchEvent({ type: Index.eventType.mapDomResize, width, height });
      }
    };

    _layerDataChange() {
      this.dispatchEvent({ type: Index.eventType.layerDataChange, layerData: this._layer.getData() });
    }

    _resetElements() {
      this._elements.length = 0;
      const lineElements = this._layer.getLineElements();
      lineElements.forEach((lineElm) => {
        this._elements.push(lineElm);
      });
      this._layer.traverse((child) => {
        const { props } = child;
        if (props && elementTypes.includes(props.type) && props.interactive && props.visible) {
          // if (props.type === 'trackMark') {
          const label = child.getLabel();
          label && child.add(label);
          // }
          this._elements.push(child);
        }
      });
    }

    _getNodeByPropsId(id) {
      let target = null;
      this._groupLayer.traverse((child) => {
        const { props } = child;
        if (props && props.id === id) {
          target = child;
        }
      });
      !target && console.error('Can not find node');
      return target;
    }
    // 刷新元素，如dom更新
    refreshElements() {
      this._layerDataChange();
      this._resetElements();
    }
}

export default Index;
